import { useEffect, useState } from "react";
import s from "./teacher-packs.module.scss";
import { useParams, useSearchParams, NavLink, useNavigate } from "react-router-dom";
import useMedia from "use-media";
import { motion, AnimatePresence } from "framer-motion";
import { useTeacher } from "@entities/teacher/api/useTeacher";
import { FetchingDiv } from "@shared/ui/fetching-div/fetching-div";
import { TeacherInfo } from "@widgets/student/ui/teacher-info/teacher-info";
import { TeacherPacks } from "@widgets/student/ui/teacher-profile/ui/teacher-packs/teacher-packs";
import { PayCard } from "@widgets/student/ui/pay-card/pay-card";
import { useAvaiableTeacherPacks } from "@widgets/student/ui/teacher-profile/api/useAvaiableTeacherPacks";
import { packages, classes } from "@shared/api/services";
import { queryClient } from "@shared/api/query-client";
import { Button } from "@shared/ui/button/button";
import { useActivePacks } from "@shared/api/useActivePacks";
import { IconArrowLeft, IconCalendar, IconX, IconBeach } from "@tabler/icons-react";
import { toast } from "react-toastify";

export const TeacherPacksPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: teacher, isFetched: isTeacherFetched } = useTeacher(id);
  const { data: packs, isFetched: isPacksFetched } = useAvaiableTeacherPacks(id);
  const { data: activePacks, isFetched: isActivePacksFetched } = useActivePacks();

  const [selectedPackId, setSelectedPackId] = useState(null);
  const [packLoading, setPackLoading] = useState(false);
  const [isCheckoutOpen, set_isCheckoutOpen] = useState(false);

  const isMobile = useMedia({ maxWidth: "768px" });

  useEffect(() => {
    if (isTeacherFetched && teacher?.trial_enabled) {
      navigate(`/teacher/booking/${id}`, { replace: true });
    }
  }, [isTeacherFetched, teacher?.trial_enabled, id, navigate]);

  useEffect(() => {
    const packParam = searchParams.get("pack");
    if (packParam) setSelectedPackId(Number(packParam));
  }, [searchParams]);

  const singleLessonPrice = teacher?.grade?.price;
  const selectedPack = packs?.find((p) => p?.id === selectedPackId);
  const economy =
    selectedPack && singleLessonPrice && selectedPack?.lessons_count
      ? singleLessonPrice * selectedPack.lessons_count - selectedPack.price
      : null;

  const activePackWithTeacher = activePacks?.data?.find(
    (pack) =>
      String(pack?.teacher?.teacher_slug) === String(id) &&
      (pack?.lessons_used ?? 0) < (pack?.lessons_count ?? 0)
  );
  const hasActivePackWithTeacher = !!activePackWithTeacher;
  // В отпуске покупка новых пакетов запрещена (офферы скрыты)
  const vacationBlocked = !!teacher?.vacation_enabled && !hasActivePackWithTeacher;

  useEffect(() => {
    if (hasActivePackWithTeacher) {
      setSelectedPackId(null);
      if (searchParams.has("pack")) {
        const next = new URLSearchParams(searchParams);
        next.delete("pack");
        setSearchParams(next, { replace: true });
      }
    }
  }, [hasActivePackWithTeacher, searchParams, setSearchParams]);

  const handleSelectPack = (pack) => {
    if (hasActivePackWithTeacher) return;
    setSelectedPackId((prev) => (prev === pack?.id ? null : pack?.id));
  };

  const handleBookByPack = () => {
    const instrumentId = activePackWithTeacher?.instrument?.id;
    navigate(`/teacher/booking/${id}${instrumentId ? `?instrument=${instrumentId}` : ""}`);
  };

  const handlePayPack = () => {
    if (!selectedPackId || hasActivePackWithTeacher) {
      if (hasActivePackWithTeacher) {
        toast.error("У вас уже есть активный пакет у этого преподавателя");
      }
      return;
    }
    setPackLoading(true);
    packages
      .bookingPack(selectedPackId)
      .then(({ data }) => {
        if (data?.success) return classes.payForAppointment(data?.order?.id);
        setPackLoading(false);
      })
      .then(async (payment) => {
        await queryClient.refetchQueries({ queryKey: ["active-packs"] });
        if (payment?.data?.payment_url) {
          // Спиннер крутится до перехода на страницу оплаты
          window.open(payment?.data?.payment_url, "_self");
        } else {
          setPackLoading(false);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Возникла ошибка");
        setPackLoading(false);
      });
  };

  const checkoutDisabled = !selectedPack || hasActivePackWithTeacher || vacationBlocked;

  return (
    <>
      <FetchingDiv
        className={s.wrapper}
        isFetching={!isTeacherFetched || !isPacksFetched || !isActivePacksFetched}
      >
				<button
          type="button"
          className={s.backButton}
          onClick={() => navigate(-1)}
        >
          <IconArrowLeft size={18} />
          Назад
        </button>
        <div className={s.title}>
          {hasActivePackWithTeacher ? "Активный пакет" : "Купить пакет занятий"}
        </div>
        <div className={s.flex}>
          <div className={s.card}>
            <TeacherInfo variant="plain" />
            <NavLink to={`/teacher/booking/${id}`} className={s.singleLessonLink}>
              <IconArrowLeft size={18} />
              Забронировать одиночное занятие
            </NavLink>
            <p className={s.description}>
              Выберите подходящий пакет, планируйте занятия и экономьте на каждом уроке.
            </p>
            {hasActivePackWithTeacher ? (
              <div className={s.activePack}>
                <div className={s.activePackHeader}>
                  <div className={s.activePackIcon}>
                    <IconCalendar size={24} />
                  </div>
                  <div className={s.activePackInfo}>
                    <div className={s.activePackTitle}>У тебя уже есть активный пакет</div>
                    <div className={s.activePackSubtitle}>
                      {activePackWithTeacher?.lessons_used || 0} из{" "}
                      {activePackWithTeacher?.lessons_count} занятий использовано
                    </div>
                  </div>
                </div>
                <Button
                  className={s.activePackButton}
                  label="Записаться по пакету"
                  onClick={handleBookByPack}
                />
              </div>
            ) : vacationBlocked ? (
              <div className={s.vacationNotice}>
                <IconBeach size={24} className={s.vacationNoticeIcon} />
                <div className={s.vacationNoticeTitle}>
                  Покупка пакетов временно недоступна
                </div>
                <div className={s.vacationNoticeText}>
                  Преподаватель находится в отпуске и временно не принимает запись. Попробуйте зайти позже.
                </div>
              </div>
            ) : (
              <TeacherPacks
                teacher={id}
                packs={packs}
                singleLessonPrice={singleLessonPrice}
                selectedId={selectedPackId}
                selectable
                variant="cards"
                onSelect={handleSelectPack}
              />
            )}
           
          </div>
          {!isMobile && (
            <div className={s.rightColumn}>
              {selectedPack && !hasActivePackWithTeacher && (
                <PayCard
                  pack={selectedPack}
                  economy={economy}
                  singleLessonPrice={singleLessonPrice}
                  onPayPack={handlePayPack}
                  packLoading={packLoading}
                />
              )}
            </div>
          )}
        </div>
      </FetchingDiv>

      {isMobile && !vacationBlocked && (
        <div className={s.mobileCheckoutBar}>
          <Button
            label="Перейти к оплате"
            onClick={() => set_isCheckoutOpen(true)}
            className={s.mobileCheckoutBtn}
            disabled={checkoutDisabled}
          />
        </div>
      )}

      <AnimatePresence>
        {isMobile && isCheckoutOpen && (
          <motion.div
            className={s.checkoutSheetOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <motion.div
              className={s.checkoutSheetBackdrop}
              onClick={() => set_isCheckoutOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            />
            <motion.div
              className={s.checkoutSheet}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className={s.checkoutSheetHeader}>
                <span className={s.checkoutSheetTitle}>Оплата пакета</span>
                <button
                  type="button"
                  className={s.checkoutSheetClose}
                  onClick={() => set_isCheckoutOpen(false)}
                  aria-label="Закрыть"
                >
                  <IconX size={24} />
                </button>
              </div>
              <div className={s.checkoutSheetBody}>
                {selectedPack && !hasActivePackWithTeacher && (
                  <PayCard
                    pack={selectedPack}
                    economy={economy}
                    singleLessonPrice={singleLessonPrice}
                    onPayPack={handlePayPack}
                    packLoading={packLoading}
                  />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
